/**
 * Restaurant Favorites Service
 * Handles all operations for user restaurant preferences (favorites and avoid list)
 */

import { supabase } from '../utils/supabase';
import { logger } from '../utils/logger';

class RestaurantFavoritesService {

  /**
   * Get or create a restaurant from Yelp data
   * Uses the Postgres function to prevent duplicates
   */
  async getOrCreateRestaurant(yelpRestaurant) {
    try {
      // Extract data from Yelp restaurant object
      const yelpBusinessId = yelpRestaurant.id || yelpRestaurant.yelp_business_id;

      // Parse address
      let address = '';
      let city = '';
      let state = '';

      if (yelpRestaurant.location) {
        address = yelpRestaurant.location.address1 || yelpRestaurant.address || '';
        city = yelpRestaurant.location.city || yelpRestaurant.city || '';
        state = yelpRestaurant.location.state || yelpRestaurant.state || '';
      } else if (yelpRestaurant.address) {
        address = yelpRestaurant.address;
        city = yelpRestaurant.city || '';
        state = yelpRestaurant.state || '';
      }

      // Call the Postgres function
      const { data, error } = await supabase.rpc('get_or_create_restaurant', {
        p_yelp_business_id: yelpBusinessId,
        p_name: yelpRestaurant.name,
        p_address: address,
        p_city: city,
        p_state: state,
        p_phone: yelpRestaurant.phone || yelpRestaurant.display_phone || null,
        p_cuisine_type: yelpRestaurant.cuisineType || yelpRestaurant.categories?.[0]?.title || 'Restaurant',
        p_price_range: yelpRestaurant.priceRange || yelpRestaurant.price || null,
        p_rating: yelpRestaurant.rating || null,
        p_review_count: yelpRestaurant.reviewCount || yelpRestaurant.review_count || null,
        p_image_url: yelpRestaurant.imageUrl || yelpRestaurant.image_url || null,
        p_yelp_url: yelpRestaurant.url || null,
        p_delivery_available: yelpRestaurant.deliveryAvailable !== undefined ? yelpRestaurant.deliveryAvailable : true,
        p_coordinates: yelpRestaurant.coordinates ? JSON.stringify(yelpRestaurant.coordinates) : null,
        p_categories: yelpRestaurant.categories ? JSON.stringify(yelpRestaurant.categories) : null,
        p_yelp_data: JSON.stringify(yelpRestaurant)
      });

      if (error) throw error;
      return data; // Returns UUID of restaurant
    } catch (error) {
      logger.error('Error in getOrCreateRestaurant:', error);
      throw error;
    }
  }

  /**
   * Add a restaurant to user's favorites or avoid list
   */
  async addFavorite(userId, yelpRestaurant, preferenceType = 'favorite', notes = '') {
    try {
      // First, get or create the restaurant
      const restaurantId = await this.getOrCreateRestaurant(yelpRestaurant);

      // Then add to favorites
      const { data, error } = await supabase
        .from('restaurant_favorites')
        .insert({
          user_id: userId,
          restaurant_id: restaurantId,
          preference_type: preferenceType,
          notes: notes
        })
        .select()
        .single();

      if (error) {
        // If duplicate, update instead
        if (error.code === '23505') {
          return await this.updateFavorite(userId, restaurantId, { preference_type: preferenceType, notes });
        }
        throw error;
      }

      return data;
    } catch (error) {
      logger.error('Error adding favorite:', error);
      throw error;
    }
  }

  /**
   * Update a favorite (notes or preference type)
   */
  async updateFavorite(userId, restaurantId, updates) {
    try {
      const { data, error } = await supabase
        .from('restaurant_favorites')
        .update(updates)
        .eq('user_id', userId)
        .eq('restaurant_id', restaurantId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error updating favorite:', error);
      throw error;
    }
  }

  /**
   * Remove a restaurant from favorites
   */
  async removeFavorite(userId, restaurantId) {
    try {
      const { error } = await supabase
        .from('restaurant_favorites')
        .delete()
        .eq('user_id', userId)
        .eq('restaurant_id', restaurantId);

      if (error) throw error;
      return true;
    } catch (error) {
      logger.error('Error removing favorite:', error);
      throw error;
    }
  }

  /**
   * Get all favorites for a user (with full restaurant details)
   */
  async getFavorites(userId, preferenceType = null) {
    try {
      let query = supabase
        .from('restaurant_favorites')
        .select(`
          *,
          restaurant:restaurants (*)
        `)
        .eq('user_id', userId);

      if (preferenceType) {
        query = query.eq('preference_type', preferenceType);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Error getting favorites from Supabase:', error);
        throw error;
      }

      logger.debug(`✅ Loaded ${data?.length || 0} favorites for user ${userId}`, data);
      return data;
    } catch (error) {
      logger.error('Error getting favorites:', error);
      throw error;
    }
  }

  /**
   * Get user's favorite restaurants only
   */
  async getFavoriteRestaurants(userId) {
    return await this.getFavorites(userId, 'favorite');
  }

  /**
   * Get user's restaurants to avoid
   */
  async getAvoidRestaurants(userId) {
    return await this.getFavorites(userId, 'avoid');
  }

  /**
   * Check if a restaurant is in user's favorites
   */
  async isFavorite(userId, yelpBusinessId) {
    try {
      // First find the restaurant
      const { data: restaurant } = await supabase
        .from('restaurants')
        .select('id')
        .eq('yelp_business_id', yelpBusinessId)
        .single();

      if (!restaurant) return false;

      // Then check if favorited
      const { data, error } = await supabase
        .from('restaurant_favorites')
        .select('id, preference_type')
        .eq('user_id', userId)
        .eq('restaurant_id', restaurant.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
      return data ? data.preference_type : null;
    } catch (error) {
      logger.error('Error checking if favorite:', error);
      return null;
    }
  }

  /**
   * Increment booking counter when a meeting is booked
   */
  async recordBooking(userId, restaurantId) {
    try {
      const { error } = await supabase.rpc('increment', {
        row_id: restaurantId,
        user_id: userId
      });

      if (error) {
        // Fallback: manual update
        const { data: current } = await supabase
          .from('restaurant_favorites')
          .select('times_booked')
          .eq('user_id', userId)
          .eq('restaurant_id', restaurantId)
          .single();

        if (current) {
          await supabase
            .from('restaurant_favorites')
            .update({
              times_booked: (current.times_booked || 0) + 1,
              last_booked_date: new Date().toISOString()
            })
            .eq('user_id', userId)
            .eq('restaurant_id', restaurantId);
        }
      }

      return true;
    } catch (error) {
      logger.error('Error recording booking:', error);
      return false;
    }
  }
}

export default new RestaurantFavoritesService();
